/**
 * B523 — the booking sheet's shell (368-2 / 370-2 / 370-61): the header
 * with the step's title and the numbered trail, the ✕ that posts `cancel`
 * to the host, and the hold strip the moment a hold exists. Drawn by the
 * STOREFRONT — inside the frame — so every piece wears the tenant theme
 * and the frame's width (the loader keeps only a fallback ✕ until ready).
 *
 * The trail is a nav for COMPLETED steps only (the item's rule). "Day &
 * time" completes with the tickets step: on the wide sheet both live on
 * one screen (368-2 draws the steppers left and the calendar right), so
 * the trail never holds it active — it turns done when a departure is
 * picked. The Done face drops the trail: nothing is left to navigate.
 *
 * B545 — the hooks are the Cart sidecar's names (redline-embed-cart.json):
 * the shell is `embed-sheet`, its parts `embed-sheet-header|-title|-trail|
 * -close|-strip|-strip-text`.
 */
import type { ReactElement, ReactNode } from "react";
import { HoldStrip, SheetHead, StepTrail } from "@transact/ui";

export type DialogStep = "tickets" | "cart" | "pay" | "done";

/** B549 — the sheet's group walk: the interrupt lives ON the tickets step;
 * the form and its sent face replace the buying trail entirely (446:2:
 * "a guest who came here from the interrupt is no longer buying tickets"). */
export type GroupStage = "form" | "sent";

const GROUP_TRAIL = [
  { key: "tickets", label: "Tickets" },
  { key: "about", label: "About your group" },
  { key: "sent", label: "Sent" },
] as const;

const TRAIL = [
  { key: "tickets", label: "Tickets" },
  { key: "when", label: "Day & time" },
  { key: "cart", label: "Cart" },
  { key: "pay", label: "Pay" },
] as const;

/** The trail's active key per step — the tickets screen holds steps 1–2. */
const ACTIVE: Record<Exclude<DialogStep, "done">, string> = {
  tickets: "tickets",
  cart: "cart",
  pay: "pay",
};

const TITLE: Record<DialogStep, string> = {
  tickets: "Tickets",
  cart: "Your cart",
  pay: "Pay",
  done: "You're booked",
};

export function DialogShell(props: {
  readonly step: DialogStep;
  /** B549 — the inquiry walk, when the buyer took the interrupt's door. */
  readonly groupStage?: GroupStage | undefined;
  /** B549 — the page whose sidecar names this state's hooks (444:2/446:2). */
  readonly redlinePage?: string | undefined;
  /** ‹ in the header — absent on the first step (nowhere back to go). */
  readonly onBack?: (() => void) | undefined;
  /** A completed trail step pressed — never fired for current or future. */
  readonly onVisit?: ((key: string) => void) | undefined;
  /** The ✕ — posts `cancel` through the bridge; the host closes the sheet. */
  readonly onClose: () => void;
  /** Milliseconds left on the live hold; absent = no hold, no strip (§10). */
  readonly holdMsLeft?: number | undefined;
  readonly children?: ReactNode;
}): ReactElement {
  /* B549 — the sidecar's names for THIS state: the group frames prefix
     every shell hook with their page name; every other state keeps B545's. */
  const page = props.redlinePage;
  const hook = (part: string): string => (page === undefined ? `embed-sheet-${part}` : `${page}-${part}`);
  const group = props.groupStage;
  const center =
    group !== undefined ? (
      <StepTrail steps={GROUP_TRAIL} activeKey={group === "sent" ? "sent" : "about"} redline={hook("trail")} />
    ) : props.step === "done" ? undefined : (
      <StepTrail
        steps={TRAIL}
        activeKey={ACTIVE[props.step]}
        {...(props.onVisit !== undefined ? { onVisit: props.onVisit } : {})}
        redline={hook("trail")}
      />
    );
  return (
    <div className="dialog-shell" data-redline="embed-sheet">
      <SheetHead
        title={group !== undefined ? "Group inquiry" : TITLE[props.step]}
        redline={page === undefined ? "embed-sheet-header" : `${page}-sheet-header`}
        titleRedline={hook("title")}
        closeRedline={hook("close")}
        {...(page !== undefined ? { backRedline: hook("back") } : {})}
        {...(props.onBack !== undefined ? { onBack: props.onBack } : {})}
        onClose={props.onClose}
        {...(center !== undefined ? { center } : {})}
      />
      {props.holdMsLeft !== undefined && props.holdMsLeft > 0 ? (
        <HoldStrip msLeft={props.holdMsLeft} redline="embed-sheet-strip" textRedline="embed-sheet-strip-text" />
      ) : null}
      {props.children !== undefined ? <div className="dialog-shell-content">{props.children}</div> : null}
    </div>
  );
}
